import { useEffect, useState } from "react";
import {
  invoicePdfModelFromSource, invoiceFileName, isCurrency, renderInvoicePdf,
  type Currency, type InvoiceDocSource, type InvoicePdfModel, type PdfImage, type SnapshotBank, type SnapshotParty,
} from "@talentkontor/domain";
import { api } from "../data";
import { publicLogoUrl } from "../components/shell/branding";

/**
 * Beleg im Portal (Agentur und Creator): aus der gespeicherten Rechnung das Modell für Ansicht, Druck und PDF
 * (invoicePdfModelFromSource). Festgeschriebene Rechnungen lesen nur die eingefrorenen Parteien; Entwürfe die aktuellen
 * Stammdaten (dieselben Regeln wie app.org_party, app.talent_party, app.brand_party in der Datenbank).
 */

type Row = Record<string, unknown>;
const s = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

/** Agentur als Partei (wie app.org_party, 0055) */
export function orgPartyOf(settings: Row | null | undefined, legal: Row | null | undefined, fallbackName: string): SnapshotParty {
  const st = settings ?? {};
  const l = legal ?? {};
  return {
    name: s(st.legal_name) ?? fallbackName, street: s(st.street), postal_code: s(st.postal_code), city: s(st.city), country: s(st.country),
    vat_id: s(st.vat_id), tax_number: s(st.tax_number), email: s(st.reply_to_email), representative: s(l.representative_name),
    representative_position: s(l.representative_position), legal_form: s(l.legal_form), small_business: st.small_business === true ? true : null,
    register: registerText(s(l.register_type), s(l.register_number), s(l.register_court)),
    logo_path: s(st.logo_path), accent: s(st.brand_color),
  };
}

/** Creator als Partei: Name der Person, mit eigener Firma die Firma, vertreten durch die Person (wie app.talent_party, 0055) */
export function talentPartyOf(t: Row | null | undefined): SnapshotParty | null {
  if (!t) return null;
  const person = [s(t.first_name), s(t.last_name)].filter(Boolean).join(" ");
  const company = s(t.company_name);
  const form = s(t.legal_form);
  const name = company ? (form && !company.toLowerCase().includes(form.toLowerCase()) ? `${company} ${form}` : company) : person;
  return {
    name, representative: company ? person : null, register: company ? s(t.register_info) : null,
    street: s(t.street), postal_code: s(t.postal_code), city: s(t.city), country: s(t.country), vat_id: s(t.vat_id), tax_number: s(t.tax_number),
    email: s(t.email), small_business: t.small_business === true,
  };
}

/** Marke als Empfänger: Rechnungsdaten der Kooperation vor Stammdaten; Referenz der Kooperation vor Käuferreferenz der Marke (0066) */
export function brandPartyOf(b: Row | null | undefined, deal: Row | null | undefined): SnapshotParty | null {
  if (!b) return null;
  const d = deal ?? {};
  return {
    name: s(d.billing_legal_name) ?? s(b.legal_name) ?? s(b.name), street: s(d.billing_street) ?? s(b.street),
    postal_code: s(d.billing_postal_code) ?? s(b.postal_code), city: s(d.billing_city) ?? s(b.city), country: s(d.billing_country) ?? s(b.country),
    vat_id: s(d.billing_vat_id) ?? s(b.vat_id), register: s(d.billing_register) ?? s(b.register_info), email: s(d.billing_email) ?? s(b.invoice_email),
    reference: s(d.billing_reference) ?? s(b.buyer_reference),
  };
}

/**
 * Konto der Agentur für den Beleg wie app.issuer_bank (0069): Bankverbindung samt QR-IBAN und der QR-Einstellung der Agentur
 * (org_settings.qr_bill_enabled, qr_bill_reference, qr_reference_prefix). Für Entwürfe; festgeschriebene Rechnungen lesen den Schnappschuss.
 */
export function orgBankOf(b: { holder: string; iban: string; bic: string | null; bank_name: string | null; qr_iban?: string | null } | null | undefined, settings: Row | null | undefined): SnapshotBank | null {
  if (!b) return null;
  const st = settings ?? {};
  return {
    method: "sepa", holder: b.holder, iban: b.iban, bic: b.bic, bank_name: b.bank_name, qr_iban: s(b.qr_iban),
    qr: { enabled: st.qr_bill_enabled !== false, reference: st.qr_bill_reference === "non" ? "non" : "scor", prefix: s(st.qr_reference_prefix) },
  };
}

/** Auszahlungsweg des Creators (talent_payout_details) wie app.issuer_bank */
export function payoutBankOf(p: Row | null | undefined, holderFallback: string | null): SnapshotBank | null {
  if (!p) return null;
  return {
    method: (s(p.payout_method) as SnapshotBank["method"]) ?? "sepa", holder: s(p.bank_account_holder) ?? holderFallback, iban: s(p.iban), bic: s(p.bic),
    bank_name: s(p.bank_name), bank_country: s(p.bank_country), account: s(p.account_number), routing: s(p.routing_code), email: s(p.payout_email),
  };
}

export interface DocInputs {
  invoice: Row;
  lines: readonly { description: string; quantity: number | null; unit_cents: number; net_cents: number; tax_rate_bp: number | null }[];
  /** aktuelle Parteien (nur für Entwürfe und Belege ohne Schnappschuss) */
  org: SnapshotParty;
  talent: SnapshotParty | null;
  brand: SnapshotParty | null;
  bankFallback: SnapshotBank | null;
  noticeFallback: string | null;
  collectiveMonths: readonly string[];
  original: { number: string | null; issue_date: string | null; paid_out_cents?: number | null } | null;
  language: "de" | "en";
  logo?: PdfImage | null;
}

const frozenStatuses = new Set(["approved", "sent", "paid", "cancelled"]);

export function docSourceOf(x: DocInputs): InvoiceDocSource {
  const i = x.invoice;
  const kind = i.kind as InvoiceDocSource["kind"];
  const agency = kind === "agency_to_brand" || kind === "agency_to_talent";
  const frozen = frozenStatuses.has(String(i.status));
  const snapIssuer = (i.issuer_snapshot as SnapshotParty | null) ?? null;
  const snapRecipient = (i.recipient_snapshot as SnapshotParty | null) ?? null;
  const issuer = frozen && snapIssuer ? snapIssuer : agency ? x.org : (x.talent ?? {});
  const recipient = snapRecipient ?? (kind === "talent_to_agency" ? x.org : kind === "agency_to_talent" ? (x.talent ?? {}) : (x.brand ?? {}));
  const self = !!i.self_billed;
  return {
    kind, number: s(i.number), draft: i.status === "draft", issueDate: s(i.issue_date), servicePeriodStart: s(i.service_period_start),
    servicePeriodEnd: s(i.service_period_end), revenueMonth: s(i.revenue_month), collectiveMonths: x.collectiveMonths,
    currency: (isCurrency(i.currency) ? i.currency : "EUR") as Currency, netCents: Number(i.net_cents ?? 0), taxCents: Number(i.tax_cents ?? 0),
    grossCents: Number(i.gross_cents ?? 0), taxMode: s(i.tax_mode), documentDueDate: s(i.document_due_date), effectiveDueDate: s(i.effective_due_date),
    issuerDueDate: s(i.issuer_due_date), paymentTermDays: typeof i.payment_term_days === "number" ? i.payment_term_days : null,
    language: x.language, selfBilled: self, creditFor: x.original ? { number: x.original.number, date: x.original.issue_date, paidOutCents: x.original.paid_out_cents ?? null } : null,
    // Gutschrift: die Agentur gestaltet den Beleg (Logo, Farbe) auch vor dem Festschreiben
    issuer: self && !(frozen && snapIssuer) ? { ...issuer, agency: { logo_path: x.org.logo_path ?? null, accent: x.org.accent ?? null, name: x.org.name ?? null } } : issuer,
    recipient,
    lines: x.lines.map((l) => ({ description: l.description, quantity: l.quantity, unitCents: l.unit_cents, netCents: l.net_cents, taxRateBp: l.tax_rate_bp })),
    // Eingefrorene Bank und Hinweis haben Vorrang; ohne sie (Entwurf, alter Beleg) gelten die heutigen Angaben
    // (ein festgeschriebener Beleg ohne eingefrorene Bank bekommt nachträglich keinen QR-Zahlteil: das archivierte PDF hatte keinen)
    bankFallback: frozen && snapIssuer?.bank ? null : frozen && x.bankFallback ? { ...x.bankFallback, qr: null } : x.bankFallback,
    noticeFallback: x.noticeFallback,
    accentFallback: agency || self ? x.org.accent ?? null : null,
    logo: x.logo ?? null,
    taxEur: taxEurOf(i),
  };
}

/** 0267 (K-296): Umsatzsteuer in Euro, wie sie die Datenbank beim Festschreiben eingefroren hat */
export function taxEurOf(i: Row): InvoiceDocSource["taxEur"] {
  const cents = i.tax_eur_cents, rate = i.tax_eur_rate, on = s(i.tax_eur_rate_on);
  if (cents === null || cents === undefined || rate === null || rate === undefined || !on) return null;
  return { cents: Number(cents), rate: Number(rate), on: on.slice(0, 10), source: s(i.tax_eur_source) };
}

export function invoiceModelOf(x: DocInputs): InvoicePdfModel {
  return invoicePdfModelFromSource(docSourceOf(x));
}

export function invoicePdfOf(model: InvoicePdfModel): { bytes: Uint8Array; filename: string } {
  return {
    bytes: renderInvoicePdf(model),
    filename: invoiceFileName({ number: model.number, issuerName: model.issuer.name, recipientName: model.recipient.name, language: model.language,
      creditNote: !!model.creditNote, selfBilling: !!model.selfBilling }),
  };
}

/** Dateiname für den Speicher: nur ASCII-Buchstaben, Ziffern, Punkt, Minus, Unterstrich */
export function storageSafeName(name: string): string {
  return name.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/ß/g, "ss").replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 120) || "Rechnung.pdf";
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

// ── Logo der Agentur: als Bild im Beleg und als JPEG im PDF ─────────────────────
const logoCache = new Map<string, Promise<{ url: string; image: PdfImage | null } | null>>();

async function loadLogo(path: string): Promise<{ url: string; image: PdfImage | null } | null> {
  // Bucket branding ist öffentlich: ohne Anmeldung (Signierseite, Freigabe, Bericht) über die öffentliche Adresse
  const direct = api.mode === "demo" ? null : publicLogoUrl(path);
  const blob = await api.download("branding", path).catch(() => null)
    ?? (direct ? await fetch(direct).then((r) => (r.ok ? r.blob() : null)).catch(() => null) : null);
  if (!blob || !blob.type.startsWith("image/")) return null;
  const url = URL.createObjectURL(blob);
  let image: PdfImage | null = null;
  try {
    const bmp = await createImageBitmap(blob);
    const scale = Math.min(1, 600 / Math.max(bmp.width, bmp.height));
    const w = Math.max(1, Math.round(bmp.width * scale)), h = Math.max(1, Math.round(bmp.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      // Transparenz auf Weiß (JPEG kennt keine Transparenz)
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(bmp, 0, 0, w, h);
      const jpeg = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", 0.9));
      if (jpeg) image = { jpeg: new Uint8Array(await jpeg.arrayBuffer()), width: w, height: h };
    }
  } catch {
    image = null;
  }
  return { url, image };
}

export function invoiceLogo(path: string | null | undefined): Promise<{ url: string; image: PdfImage | null } | null> {
  if (!path) return Promise.resolve(null);
  let p = logoCache.get(path);
  if (!p) {
    p = loadLogo(path).catch(() => null);
    logoCache.set(path, p);
  }
  return p;
}

export function useInvoiceLogo(path: string | null | undefined): { url: string; image: PdfImage | null } | null {
  const [logo, setLogo] = useState<{ url: string; image: PdfImage | null } | null>(null);
  useEffect(() => {
    let alive = true;
    void invoiceLogo(path).then((l) => { if (alive) setLogo(l); });
    return () => { alive = false; };
  }, [path]);
  return logo;
}

/**
 * Registerangabe für Fußzeilen (K-404): „HRB 98765 Amtsgericht Düsseldorf“. Enthält die Nummer die Registerart schon
 * („HRB 98765“), steht die Art nicht noch einmal davor (vorher „HRB HRB 98765“).
 */
export function registerText(type: string | null | undefined, number: string | null | undefined, court: string | null | undefined): string | null {
  const art = (type ?? "").trim();
  const nr = (number ?? "").trim();
  const doppelt = !!art && !!nr && nr.toLowerCase().replace(/[\s.]/g, "").startsWith(art.toLowerCase().replace(/[\s.]/g, ""));
  return [doppelt ? null : art, nr, (court ?? "").trim()].filter(Boolean).join(" ") || null;
}
